'use client';

import * as React from 'react';
import { useTenant } from '@/hooks/use-tenant';
import {
    useGetProjectNotesQuery,
    useCreateProjectNoteMutation,
    useUpdateProjectNoteMutation,
    useDeleteProjectNoteMutation,
    useShareProjectNoteMutation,
    useGetExternalProjectNotesQuery,
    useCreateExternalProjectNoteMutation,
    useUpdateExternalProjectNoteMutation,
    useDeleteExternalProjectNoteMutation,
} from '@/state/project/project-api';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    // CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
    Loader2,
    Plus,
    Trash2,
    StickyNote,
    Clock,
    User,
    Pencil,
    Share2,
    Lock,
    Unlock,
    Search,
    Maximize2,
} from 'lucide-react';
import type { ProjectNote } from '@/types';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import { formatDate } from 'date-fns';
// import { DATE_FORMAT_WITH_TIME } from '@/constants';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { NoteEditorModal } from './note-editor-modal';
import { NoteViewModal } from './note-view-modal';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export const ProjectNotes = ({
    projectId,
    isExternal = false,
    resourceId,
    isReadOnly = false,
}: {
    projectId: number;
    isExternal?: boolean;
    resourceId?: number;
    isReadOnly?: boolean;
}) => {
    const { tenant } = useTenant();
    const [searchTerm, setSearchTerm] = React.useState('');
    const [isEditorOpen, setIsEditorOpen] = React.useState(false);
    const [editingNote, setEditingNote] = React.useState<ProjectNote | undefined>();
    const [viewingNote, setViewingNote] = React.useState<ProjectNote | undefined>();

    // Hooks for Internal Projects
    const { data: internalNotes, isLoading: internalLoading } = useGetProjectNotesQuery(
        { tenant: tenant!, projectId },
        { skip: !tenant || isExternal }
    );
    const [createInternalNote, { isLoading: isCreatingInternal }] = useCreateProjectNoteMutation();
    const [updateInternalNote, { isLoading: isUpdatingInternal }] = useUpdateProjectNoteMutation();
    const [deleteInternalNote] = useDeleteProjectNoteMutation();
    const [shareInternalNote] = useShareProjectNoteMutation();

    // Hooks for External Projects
    const { data: externalNotes, isLoading: externalLoading } = useGetExternalProjectNotesQuery(
        { projectId: resourceId || projectId },
        { skip: !isExternal || (!resourceId && !projectId) }
    );
    const [createExternalNote, { isLoading: isCreatingExternal }] = useCreateExternalProjectNoteMutation();
    const [updateExternalNote, { isLoading: isUpdatingExternal }] = useUpdateExternalProjectNoteMutation();
    const [deleteExternalProjectNote] = useDeleteExternalProjectNoteMutation();

    const notes = isExternal ? externalNotes : internalNotes;
    const notesLoading = isExternal ? externalLoading : internalLoading;
    const isCreating = isExternal ? isCreatingExternal : isCreatingInternal;
    const isUpdating = isExternal ? isUpdatingExternal : isUpdatingInternal;

    const filteredNotes = React.useMemo(() => {
        if (!notes) return [];
        return notes.filter(note =>
            note.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [notes, searchTerm]);

    const handleSaveNote = async (data: { title: string; note: string; personal: boolean }) => {
        try {
            if (editingNote) {
                if (isExternal) {
                    await updateExternalNote({
                        noteId: editingNote.id,
                        projectId: resourceId || projectId,
                        data: {
                            title: data.title,
                            note: data.note,
                            personal: data.personal,
                        },
                    }).unwrap();
                } else {
                    await updateInternalNote({
                        tenant: tenant!,
                        noteId: editingNote.id,
                        data: {
                            title: data.title,
                            note: data.note,
                            personal: data.personal,
                        },
                    }).unwrap();
                }
                toast.success('Note updated successfully');
            } else {
                if (isExternal) {
                    await createExternalNote({
                        data: {
                            ...data,
                            project: resourceId || projectId,
                        },
                    }).unwrap();
                } else {
                    await createInternalNote({
                        tenant: tenant!,
                        data: {
                            ...data,
                            project: projectId,
                        },
                    }).unwrap();
                }
                toast.success('Note created successfully');
            }
            setIsEditorOpen(false);
            setEditingNote(undefined);
            if (viewingNote && editingNote && viewingNote.id === editingNote.id) {
                const updatedNote = notes?.find(n => n.id === editingNote.id);
                if (updatedNote) setViewingNote(updatedNote);
            }
        } catch (error) {
            toast.error('Failed to save note');
            console.error('Save note error:', error);
        }
    };

    const handleDeleteNote = async (noteId: number) => {
        try {
            if (isExternal) {
                await deleteExternalProjectNote({ noteId, projectId: resourceId || projectId }).unwrap();
            } else {
                await deleteInternalNote({ tenant: tenant!, noteId }).unwrap();
            }
            toast.success('Note deleted');
        } catch (error) {
            toast.error('Failed to delete note');
            console.error('Delete note error:', error);
        }
    };

    const handleShareToggle = async (note: ProjectNote) => {
        if (isExternal) {
            try {
                await updateExternalNote({
                    noteId: note.id,
                    projectId: resourceId || projectId,
                    data: { personal: !note.personal }
                }).unwrap();
                toast.success(note.personal ? 'Note shared with project' : 'Note marked as private');
            } catch (error) {
                toast.error('Failed to update share status');
                console.error('Share toggle error:', error);
            }
            return;
        }

        try {
            await shareInternalNote({
                tenant: tenant!,
                noteId: note.id,
                personal: !note.personal,
            }).unwrap();
            toast.success(note.personal ? 'Note shared with project' : 'Note marked as private');
        } catch (error) {
            toast.error('Failed to update share status');
            console.error('Share toggle error:', error);
        }
    };

    if (notesLoading) {
        return (
            <div className="flex h-[400px] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-8 pb-12">
            {/* Header & Actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight">Project Notes</h2>
                    <p className="text-sm text-muted-foreground">
                        Keep track of important information and team insights.
                    </p>
                </div>
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <div className="relative flex-grow sm:flex-grow-0">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search notes..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 h-10 w-full sm:w-[250px] bg-background/50"
                        />
                    </div>
                    {!isReadOnly && (
                        <Button onClick={() => { setEditingNote(undefined); setIsEditorOpen(true); }} className="gap-2 shadow-sm transition-all hover:shadow-md h-10 shrink-0">
                            <Plus className="h-4 w-4" />
                            Add Note
                        </Button>
                    )}
                </div>
            </div>

            {/* Notes Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredNotes.length > 0 ? (
                    filteredNotes.map((note) => (
                        <Card key={note.id} className="group relative border-border/40 bg-card/80 backdrop-blur-sm transition-all duration-300 hover:border-primary/40 hover:shadow-xl flex flex-col overflow-hidden">
                            <CardHeader className="pb-3 space-y-4">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="space-y-1 min-w-0 flex-1">
                                        <CardTitle className="text-lg font-bold truncate group-hover:text-primary transition-colors">
                                            {note.title}
                                        </CardTitle>
                                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
                                            <span className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {formatDate(new Date(note.updatedAt), 'MMM d, yyyy')}
                                            </span>
                                            <span>•</span>
                                            <div className="flex items-center gap-1">
                                                {note.personal ? (
                                                    <Badge variant="outline" className="text-[9px] h-4 bg-amber-500/5 text-amber-500 border-amber-500/20 gap-1 px-1.5">
                                                        <Lock className="h-2 w-2" /> Private
                                                    </Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-[9px] h-4 bg-emerald-500/5 text-emerald-500 border-emerald-500/20 gap-1 px-1.5">
                                                        <Unlock className="h-2 w-2" /> Shared
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => setViewingNote(note)}
                                            className="h-8 w-8 text-muted-foreground hover:bg-primary/5 hover:text-primary"
                                            title="View Full Screen"
                                        >
                                            <Maximize2 className="h-3.5 w-3.5" />
                                        </Button>
                                        {!isReadOnly && (
                                            <>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleShareToggle(note)}
                                                    className="h-8 w-8 text-muted-foreground hover:bg-primary/5 hover:text-primary"
                                                    title={note.personal ? 'Share with project' : 'Make private'}
                                                >
                                                    <Share2 className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => { setEditingNote(note); setIsEditorOpen(true); }}
                                                    className="h-8 w-8 text-muted-foreground hover:bg-primary/5 hover:text-primary"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent>
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>Delete Note?</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                Are you sure you want to delete "{note.title}"? This action cannot be undone.
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                            <AlertDialogAction
                                                                onClick={() => handleDeleteNote(note.id)}
                                                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                            >
                                                                Delete
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="flex-1 overflow-hidden cursor-pointer" onClick={() => setViewingNote(note)}>
                                <div className="h-[120px] rounded-lg bg-muted/30 p-3 ring-1 ring-border/5 group-hover:bg-muted/50 transition-colors relative overflow-hidden">
                                    <ReadOnlyContentDisplay
                                        html={note.note}
                                        className="prose prose-xs dark:prose-invert max-w-none text-foreground/80 text-xs leading-relaxed"
                                    />
                                    <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-muted/30 to-transparent group-hover:from-muted/50" />
                                </div>
                                <div className="mt-4 flex items-center gap-2 text-[10px] text-muted-foreground">
                                    <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10">
                                        <User className="h-2.5 w-2.5 text-primary" />
                                    </div>
                                    <span className="truncate">
                                        {typeof note.created_by === 'object'
                                            ? note.created_by.email
                                            : note.created_by_email || note.created_by}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                ) : (
                    <div className="col-span-full flex flex-col items-center justify-center py-20 rounded-2xl border-2 border-dashed border-border/40 bg-muted/20 text-center shadow-inner">
                        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background shadow-md">
                            <StickyNote className="h-8 w-8 text-primary shadow-sm" />
                        </div>
                        <h3 className="text-lg font-bold">No notes found</h3>
                        <p className="mt-1 max-w-xs text-sm text-muted-foreground px-4">
                            {searchTerm ? `No results for "${searchTerm}". Try a different search term.` : "Start by adding your first note to this project."}
                        </p>
                        {!searchTerm && !isReadOnly && (
                            <Button
                                variant="outline"
                                onClick={() => setIsEditorOpen(true)}
                                className="mt-6 gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Add First Note
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Modal */}
            <NoteEditorModal
                isOpen={isEditorOpen}
                onClose={() => { setIsEditorOpen(false); setEditingNote(undefined); }}
                onSave={handleSaveNote}
                initialData={editingNote}
                isSaving={isCreating || isUpdating}
            />

            {/* View Modal */}
            <NoteViewModal
                isOpen={!!viewingNote}
                note={viewingNote}
                onClose={() => setViewingNote(undefined)}
                onEdit={(note) => {
                    if (isReadOnly) return;
                    setEditingNote(note);
                    setIsEditorOpen(true);
                }}
                onShareToggle={(note) => {
                    if (isReadOnly) return;
                    handleShareToggle(note);
                }}
                onDelete={(note) => {
                    if (isReadOnly) return;
                    handleDeleteNote(note.id);
                }}
            />
        </div>
    );
};
